import React, { useEffect, useState } from 'react';
import { Paragraph } from '@project-stories/elements/Paragraph/Paragraph';
import { Headings } from '@arctheme-components/elements/Headings/Headings';
import { Image } from '@project-stories/elements/Image/Image';

interface FeatureBlockSettings {
  icon?: string;
  title?: string;
  description?: string;
  title_font_style?: string;
  description_font_style?: string;
  text_alignment?: 'left' | 'center' | 'right';
  column_position?: string;
  spacing?: string;
}

interface MediaBlockSettings {
  image?: string;
  video?: string;
  column_position?: string;
  column_span?: string;
}

interface FeatureBlock {
  type: 'feature_block';
  settings: FeatureBlockSettings;
}

interface MediaBlock {
  type: 'media_block';
  settings: MediaBlockSettings;
}

type Block = FeatureBlock | MediaBlock;

interface ShopifyFeaturesProps {
  settings: {
    section: {
      title?: string;
      title_font_style?: string;
      padding_top_mobile?: number;
      padding_bottom_mobile?: number;
      padding_top_desktop?: number;
      padding_bottom_desktop?: number;
      background_color?: string;
      content_width?: string;
      container_type?: string;
      reveal_animation_class?: string;
    },
    blocks: Block[];
  }
}

export const ShopifyFeatures = ({ settings }: ShopifyFeaturesProps) => {
  const { section, blocks } = settings.settings;
  const [hasShown, setHasShown] = useState(false);

  // Split blocks into left and right columns based on column_position
  const getColumnBlocks = () => {
    const leftBlocks: Block[] = [];
    const rightBlocks: Block[] = [];
    const centerBlocks: Block[] = [];
    
    blocks.forEach(block => {
      const position = block.settings.column_position || 'left';
      
      if (position === 'left') {
        leftBlocks.push(block);
      } else if (position === 'right') {
        rightBlocks.push(block);
      } else {
        centerBlocks.push(block);
      }
    });

    return { leftBlocks, rightBlocks, centerBlocks };
  };

  const { leftBlocks, rightBlocks, centerBlocks } = getColumnBlocks();

  // Render feature block with icon, title, and description
  const renderFeatureBlock = (block: FeatureBlock) => {
    const { icon, title, description, title_font_style, description_font_style, text_alignment } = block.settings;
    const alignment = text_alignment || 'left';
    
    return (
      <div className={`feature-block flex gap-3 ${alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'}`}>
        {icon && (
          <div className="feature-icon mb-4">
            <i className={`text-[42px] icon icon-${icon}`} />
          </div>
        )}
        <div className="flex flex-col">
          {title && (
            <Headings 
              text={title} 
              type="h3" 
              classes={title_font_style || 'text-xl font-bold mb-2'}
            />
          )}
          {description && (
            <Paragraph 
              text={description}
              type={description_font_style || 'text-base'}
            />
          )}
        </div>
      </div>
    );
  };

  // Render media block with image or video
  const renderMediaBlock = (block: MediaBlock) => {
    const { image, video, column_span } = block.settings;
    const spanClass = column_span || 'col-span-1';
    
    return (
      <div className={`media-block mb-8 lg:mb-0 h-full`}>
        {image && (
          <Image 
            imageUrl={image} 
            alt="Feature image" 
            isLazy={true}
            classes="w-full h-full rounded-[4px] object-cover"
          />
        )}
        {video && (
          <video 
            className="w-full h-full object-cover rounded-[4px]"
            controls
            preload="none"
            poster={image}
          >
            <source src={video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    );
  };

  return (
    <div>
     
        <div 
          className={`${section.reveal_animation_class || ''} relative w-full`}
          style={{
            backgroundColor: section.background_color || 'transparent'
          }}
        >
          <div className={`${section.container_type || 'container'} mx-auto`}>
            <div className={`w-full`}>
              {section.title && (
                <div className="section-title mb-10 lg:mb-16">
                  <Headings 
                    text={section.title} 
                    type="h2" 
                    classes={section.title_font_style || 'text-3xl lg:text-4xl font-bold text-center'}
                  />
                </div>
              )}

              <div className="flex flex-col lg:flex-row gap-12">
                {/* Left column */}
                {leftBlocks.length > 0 && (
                  <div className="flex flex-col gap-12">
                    {leftBlocks.map((block, index) => (
                      <div key={`left-block-${index}`}>
                        {block.type === 'feature_block' ? renderFeatureBlock(block as FeatureBlock) : renderMediaBlock(block as MediaBlock)}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Center column */}
                <div className="grid grid-cols-2 gap-x-6 lg:gap-x-12 gap-y-6 lg:gap-y-10 lg:basis-[90%]">
                  {centerBlocks.length > 0 && (
                    <div className="col-span-1 flex flex-col gap-6 lg:gap-10">
                      {centerBlocks.map((block, index) => (
                        <div className="flex-1" key={`center-block-${index}`}>
                          {block.type === 'feature_block' ? renderFeatureBlock(block as FeatureBlock) : renderMediaBlock(block as MediaBlock)}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Right column */}
                  {rightBlocks.length > 0 && (
                    <div className="col-span-1 flex flex-col gap-6 lg:gap-10">
                      {rightBlocks.map((block, index) => (
                        <div className="flex-1" key={`right-block-${index}`}>
                          {block.type === 'feature_block' ? renderFeatureBlock(block as FeatureBlock) : renderMediaBlock(block as MediaBlock)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      
    </div>
  );
};

export default ShopifyFeatures;